import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Globe, 
  Bell, 
  User, 
  Heart,
  Play,
  Calendar,
  MapPin,
  CreditCard,
  Shield,
  MessageCircle,
  Zap,
  BarChart3,
  ChevronRight,
  Star,
  CheckCircle,
  ArrowRight,
  Music,
  Users,
  Mic,
  Plus,
  Clock,
  MapIcon,
  Sparkles,
  TrendingUp,
  Award,
  Headphones,
  Gamepad2,
  Coffee,
  Palette,
  Briefcase,
  PartyPopper,
  Camera,
  Video,
  Ticket,
  Smartphone
} from 'lucide-react';
import EventCard from '../components/EventCard';
import Footer from '../components/Footer';
import { Card, LoadingSpinner, EmptyState } from '../components/ui';
import { eventsService, Event as ServiceEvent } from '../services/eventsService';
import { useAuth } from '../contexts/AuthContext';
import { favoritesService } from '../services/favoritesService';
import { useToast } from '../contexts/ToastContext';

interface AboutProps {
  user?: any;
}

interface Event {
  id: string;
  title: string;
  image: string;
  price: number;
  date: string;
  time: string;
  location: string;
  category: string;
  tags?: string[];
  attendees?: number;
  rating?: number;
}

const About: React.FC<AboutProps> = ({ user }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [favoriteEvents, setFavoriteEvents] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isScrolled, setIsScrolled] = useState(false);
  const [featuredEvents, setFeaturedEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { success, error: showError } = useToast();
  const isRTL = i18n.language === 'ar';

  // Enhanced scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const eventCategories = [
    { id: 'all', label: t('events.categories.all', 'All Events'), icon: Calendar, color: 'from-blue-400 to-yellow-500' },
    { id: 'music', label: t('events.categories.music', 'Music'), icon: Headphones, color: 'from-pink-400 to-red-500' },
    { id: 'sports', label: t('events.categories.sports', 'Sports'), icon: Award, color: 'from-green-400 to-emerald-500' },
    { id: 'conference', label: t('home.conferences', 'Business'), icon: Briefcase, color: 'from-indigo-400 to-blue-500' },
    { id: 'entertainment', label: t('events.categories.entertainment', 'Entertainment'), icon: PartyPopper, color: 'from-yellow-400 to-orange-500' },
    { id: 'arts', label: t('events.categories.arts', 'Arts & Culture'), icon: Palette, color: 'from-yellow-400 to-orange-500' },
    { id: 'food', label: t('events.categories.food', 'Food & Drink'), icon: Coffee, color: 'from-amber-400 to-yellow-500' },
    { id: 'tech', label: t('events.categories.tech', 'Technology'), icon: Smartphone, color: 'from-cyan-400 to-blue-500' }
  ];

  // Load events from the service
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const publishedEvents = await eventsService.getPublishedEvents();
        
        // Transform service events to Home page event format
        const transformedEvents: Event[] = publishedEvents.map(event => ({
          id: event.id,
          title: event.title,
          image: event.image,
          price: event.price,
          date: formatEventDate(event.date),
          time: event.time,
          location: event.venue,
          category: event.category,
          tags: event.tags,
          attendees: Math.floor(Math.random() * 5000) + 500, // Random attendees for demo
          rating: 4.3 + Math.random() * 0.7 // Random rating between 4.3-5.0
        }));
        
        setFeaturedEvents(transformedEvents);
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  // Helper function to format date
  const formatEventDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const trendingEvents = featuredEvents.slice(0, 3);
  const upcomingEvents = featuredEvents.slice(3, 6);

  const features = [
    {
      icon: CreditCard,
      title: t('home.features.payments.title', 'One-Tap Payments & Cashless Entry'),
      description: t('home.features.payments.description', 'Apple Pay, Google Pay, Crypto/NFT support, and local cards'),
      gradient: 'from-green-400 to-emerald-500'
    },
    {
      icon: Shield,
      title: t('home.features.nft.title', 'NFT-Backed Tickets with Resale Controls'),
      description: t('home.features.nft.description', 'Blockchain ticketing for anti-scalping and secure transfers'),
      gradient: 'from-yellow-400 to-orange-500'
    },
    {
      icon: MessageCircle,
      title: t('home.features.ai.title', 'AI Reminders via WhatsApp & App Push'),
      description: t('home.features.ai.description', 'Smart notifications and chatbot assistance'),
      gradient: 'from-blue-400 to-cyan-500'
    },
    {
      icon: Zap,
      title: t('home.features.nfc.title', 'Fast Gate Entry using NFC/RFID Access'),
      description: t('home.features.nfc.description', 'Instant entry with contactless technology'),
      gradient: 'from-yellow-400 to-orange-500'
    }
  ];

  const stats = [
    { label: 'Events Hosted', value: '10,000+', icon: Calendar },
    { label: 'Happy Customers', value: '250K+', icon: Users },
    { label: 'Cities Covered', value: '15+', icon: MapIcon },
    { label: 'Success Rate', value: '99.9%', icon: TrendingUp }
  ];

  const testimonials = [
    {
      quote: t('home.testimonials.sarah.quote', "Streamlined check-in with NFC gates made our event seamless"),
      author: t('home.testimonials.sarah.author', "Sarah Al-Mahmoud"),
      role: t('home.testimonials.sarah.role', "Event Organizer"),
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b2bb?w=100&h=100&fit=crop&crop=face'
    },
    {
      quote: t('home.testimonials.ahmed.quote', "Zero scalping—NFT resale control protects our attendees"),
      author: t('home.testimonials.ahmed.author', "Ahmed Hassan"),
      role: t('home.testimonials.ahmed.role', "Festival Director"),
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
    },
    {
      quote: "The AI recommendations helped me discover amazing local events I never would have found.",
      author: "Lina Qasemi",
      role: "Music Enthusiast",
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
    }
  ];

  const handleFavorite = async (eventId: string) => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const isFavorited = favoriteEvents.includes(eventId);
      if (isFavorited) {
        await favoritesService.removeFavorite(eventId);
        setFavoriteEvents(prev => prev.filter(id => id !== eventId));
        success('Removed from favorites');
      } else {
        await favoritesService.addFavorite(eventId);
        setFavoriteEvents(prev => [...prev, eventId]);
        success('Added to favorites');
      }
    } catch (err: any) {
      showError(err.message || 'Failed to update favorite');
    }
  };

  // Load favorites on mount
  useEffect(() => {
    const loadFavorites = async () => {
      if (isAuthenticated) {
        try {
          const userFavorites = await favoritesService.getFavorites();
          setFavoriteEvents(userFavorites.map(fav => fav.id));
        } catch (err) {
          console.error('Error loading favorites:', err);
        }
      }
    };
    loadFavorites();
  }, [isAuthenticated]);

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        {/* Enhanced Liquid Glass Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
          <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
          <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
        </div>
        
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-16 pb-8 flex flex-col min-h-screen">
          
          {/* Enhanced Hero Content */}
          <div className="flex-1 flex flex-col justify-center text-center mb-8">
            <div className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full liquid-glass text-sm font-medium mb-4 liquid-glow" style={{color: 'var(--liquid-text-secondary)'}}>
                <Sparkles size={16} className="mr-2" />
                Jordan's #1 Event Platform
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-8 leading-tight tracking-tight liquid-glow-strong">
              <span className="bg-gradient-to-r from-blue-400 via-yellow-400 to-orange-400 bg-clip-text text-transparent">
                {t('about.heroTitle', 'About ShufeShow')}
              </span><br />
              <span style={{color: 'var(--liquid-text-primary)'}}>
                {t('about.heroSubtitle', 'Revolutionizing Events in Jordan')}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-10 max-w-4xl mx-auto leading-relaxed">
              {t('about.heroDescription', 'We are Jordan\'s premier event management platform, combining cutting-edge technology with local expertise to create unforgettable experiences.')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <button 
                onClick={() => navigate('/')}
                className="glass-button px-8 py-4 rounded-2xl text-white font-bold text-lg glow flex items-center space-x-3 hover:scale-105 transition-all duration-300 shadow-2xl min-w-[200px]"
              >
                <Search size={20} />
                <span>{t('about.exploreEvents', 'Explore Events')}</span>
              </button>
              
              <button 
                className="glass-strong px-8 py-4 rounded-2xl text-white font-bold text-lg border border-white/30 hover:bg-white/20 transition-all duration-300 flex items-center space-x-3 hover:scale-105 shadow-2xl min-w-[200px]"
              >
                <Plus size={20} />
                <span>{t('about.joinOurTeam', 'Join Our Team')}</span>
              </button>
            </div>

            {/* Stats Section in Hero */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div key={index} className="text-center glass p-4 rounded-2xl hover:scale-105 transition-transform">
                    <IconComponent size={24} className="mx-auto mb-2 text-white/80" />
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-white/70 text-sm">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>


        </div>
      </div>

      {/* Featured Events Section - Enhanced */}
      <div className="py-20 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(to bottom, var(--liquid-glass-20), transparent)'}}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-white/90 text-sm font-medium mb-4">
              <Star size={16} className="mr-2" />
              Featured Events
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('home.featuredDescription', 'Discover the hottest events happening now')}
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              From international concerts to local festivals, find your next unforgettable experience
            </p>
          </div>

          {/* Trending Events */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
              <TrendingUp size={24} className="mr-3 text-green-400" />
              Trending Now
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {trendingEvents.map((event, index) => (
                <div key={event.id} className="group cursor-pointer">
                  <div className="relative overflow-hidden rounded-3xl glass-card hover:scale-105 transition-all duration-500 shadow-2xl">
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        src={event.image} 
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      
                      {/* Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full">
                          #{index + 1} TRENDING
                        </span>
                      </div>

                      {/* Favorite Button */}
                      <button 
                        onClick={() => handleFavorite(event.id)}
                        className="absolute top-4 right-4 w-10 h-10 glass-button rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform"
                      >
                        <Heart 
                          size={18} 
                          className={favoriteEvents.includes(event.id) ? 'text-red-400 fill-current' : ''}
                        />
                      </button>

                      {/* Event Info Overlay */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h4 className="text-white font-bold text-lg mb-2 line-clamp-2">{event.title}</h4>
                        <div className="flex items-center justify-between text-white/80 text-sm">
                          <div className="flex items-center space-x-2">
                            <Calendar size={14} />
                            <span>{event.date.split(',')[0]}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPin size={14} />
                            <span>{event.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {event.tags?.slice(0, 2).map((tag, tagIndex) => (
                            <span key={tagIndex} className="px-2 py-1 bg-white/10 rounded-full text-xs text-white/80">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star size={14} className="text-yellow-400 fill-current" />
                          <span className="text-white/80 text-sm">{event.rating}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-2xl font-bold text-white">${event.price}</div>
                          <div className="text-white/60 text-sm">{event.attendees?.toLocaleString()} attending</div>
                        </div>
                        <button 
                          onClick={() => navigate(`/event/${event.id}`)}
                          className="glass-button px-6 py-3 rounded-2xl text-white font-medium glow hover:scale-105 transition-transform"
                        >
                          Get Tickets
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Events */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
              <Clock size={24} className="mr-3 text-blue-400" />
              Coming Soon
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event) => (
                <div key={event.id} className="group cursor-pointer">
                  <div className="glass-card rounded-3xl overflow-hidden hover:scale-105 transition-all duration-500 shadow-xl">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={event.image} 
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      
                      <button 
                        onClick={() => handleFavorite(event.id)}
                        className="absolute top-3 right-3 w-8 h-8 glass-button rounded-full flex items-center justify-center text-white"
                      >
                        <Heart 
                          size={16} 
                          className={favoriteEvents.includes(event.id) ? 'text-red-400 fill-current' : ''}
                        />
                      </button>
                    </div>

                    <div className="p-6">
                      <h4 className="text-white font-bold text-lg mb-3 line-clamp-2">{event.title}</h4>
                      
                      <div className="space-y-2 mb-4 text-white/70 text-sm">
                        <div className="flex items-center space-x-2">
                          <Calendar size={14} />
                          <span>{event.date}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin size={14} />
                          <span>{event.location}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users size={14} />
                          <span>{event.attendees?.toLocaleString()} interested</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-xl font-bold text-white">${event.price}</div>
                        <button 
                          onClick={() => navigate(`/event/${event.id}`)}
                          className="glass-button px-4 py-2 rounded-xl text-white text-sm font-medium glow"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Enhanced */}
      <div className="py-20 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(to bottom, var(--liquid-glass-15), var(--liquid-glass-20))'}}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-yellow-500/20 text-white/90 text-sm font-medium mb-4">
              <Zap size={16} className="mr-2" />
              Next-Gen Features
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('home.whyChooseTitle', 'Why Choose Our Platform')}
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              {t('home.whyChooseDescription', 'Next-generation features that revolutionize event management and attendance')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="group">
                  <div className="glass-card p-8 rounded-3xl text-center hover:scale-105 transition-all duration-500 shadow-xl h-full">
                    <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <IconComponent size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                    <p className="text-white/70 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Testimonials Section - Enhanced */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-white/90 text-sm font-medium mb-4">
              <MessageCircle size={16} className="mr-2" />
              Customer Stories
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Loved by Event Organizers & Attendees
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="glass-card p-8 rounded-3xl hover:scale-105 transition-all duration-300 shadow-xl">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-white/90 text-lg mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center space-x-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-white font-semibold">{testimonial.author}</div>
                    <div className="text-white/60 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section - Enhanced */}
      <div className="py-20 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(to right, var(--liquid-glass-25), var(--liquid-glass-20))'}}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Create Amazing Events?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Join thousands of event organizers who trust our platform for seamless event management and ticketing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="glass-button px-8 py-4 rounded-2xl text-white font-bold text-lg glow hover:scale-105 transition-all duration-300 shadow-2xl"
            >
              Start Creating Events
            </button>
            <button 
              onClick={() => navigate('/')}
              className="glass-strong px-8 py-4 rounded-2xl text-white font-bold text-lg border border-white/30 hover:bg-white/20 transition-all duration-300 hover:scale-105 shadow-2xl"
            >
              Explore Events
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />


    </div>
  );
};

export default About;
